import { useMemo, useState } from 'react';
import { Plus, TrendingUp, EyeOff, Eye, Pencil, Check, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Select } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import { useTariffs } from '../../hooks/useTariffs';
import { useCustomers } from '../../hooks/useCustomers';
import { TariffFormModal } from '../finance/TariffFormModal';
import type { Tariff } from '../../types/finance';
import { descripcionTarifa, vigenciaTexto, mensajeErrorFinanzas, hoyISODate } from '../../utils/finance';
import type { TariffCreateDto } from '../../lib/api/endpoints';

/**
 * FE-32 — la lista de precios, para Administrador y Ejecutivo (ADR-030 §3).
 *
 * Dos reglas del contrato gobiernan la pantalla:
 *  1. **Los precios no se editan**: «Subir precios» crea la vigencia siguiente
 *     y cierra la actual. La historia de precios es la que explica lo cobrado.
 *  2. **Sin borrado**: una tarifa se desactiva. Los ingresos que la usaron
 *     conservan su foto.
 */
export function TariffsPanel({ search, soloLectura = false }: { search: string; soloLectura?: boolean }) {
  const { tariffs, isLoading, error, crear, actualizar } = useTariffs();
  const { customers } = useCustomers();
  const [clienteFiltro, setClienteFiltro] = useState('');
  const [soloVigentes, setSoloVigentes] = useState(true);
  const [formAbierto, setFormAbierto] = useState(false);
  const [base, setBase] = useState<Tariff | null>(null);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [editNombre, setEditNombre] = useState('');
  const [enCurso, setEnCurso] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const hoy = hoyISODate();
  const vigente = (t: Tariff) => t.active && t.validFrom <= hoy && (!t.validTo || hoy <= t.validTo);

  const visibles = useMemo(() => tariffs
    .filter(t => !clienteFiltro || t.customerId === clienteFiltro)
    .filter(t => !soloVigentes || vigente(t))
    .filter(t => {
      const q = search.toLowerCase();
      return !q || t.name.toLowerCase().includes(q) || t.customerName.toLowerCase().includes(q) || (t.vehicleClass ?? '').toLowerCase().includes(q);
    })
    .sort((a, b) => a.customerName.localeCompare(b.customerName) || a.name.localeCompare(b.name) || b.validFrom.localeCompare(a.validFrom)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [tariffs, clienteFiltro, soloVigentes, search, hoy]);

  const crearConCierre = async (data: TariffCreateDto, cerrar?: { id: string; validTo: string }) => {
    await crear(data);
    if (cerrar) await actualizar(cerrar.id, { validTo: cerrar.validTo });
  };

  const guardarNombre = async (id: string) => {
    if (!editNombre.trim()) { setAviso('El nombre no puede quedar vacío.'); return; }
    setEnCurso(true); setAviso(null);
    try { await actualizar(id, { name: editNombre.trim() }); setEditandoId(null); }
    catch (err) { setAviso(mensajeErrorFinanzas(err, 'tarifa')); }
    finally { setEnCurso(false); }
  };

  const alternar = async (t: Tariff) => {
    setEnCurso(true); setAviso(null);
    try { await actualizar(t.id, { active: !t.active }); }
    catch (err) { setAviso(mensajeErrorFinanzas(err, 'tarifa')); }
    finally { setEnCurso(false); }
  };

  if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><Spinner /></div>;

  return (
    <div className="expenses-touch" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
        Una tarifa es <strong>una vigencia</strong>. Para subir precios se crea la siguiente y la actual se
        cierra: cada ingreso guarda la foto de la tarifa con la que se calculó, así que la historia de
        precios se conserva. No hay borrado: se desactivan.
      </p>
      {error && <p style={{ margin: 0, fontSize: '12.5px', color: '#EF4444' }}>No se pudieron cargar las tarifas: {error.message}</p>}
      {aviso && <p style={{ margin: 0, fontSize: '12.5px', color: '#EF4444', fontWeight: 600 }}>{aviso}</p>}

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '0 1 220px', minWidth: '160px' }}>
          <Select label="Cliente" value={clienteFiltro} onChange={e => setClienteFiltro(e.target.value)}
            options={[{ value: '', label: 'Todos los clientes' }, ...customers.map(c => ({ value: c.id, label: c.name }))]} />
        </div>
        <div style={{ flex: '0 1 200px', minWidth: '160px' }}>
          <Select label="Vigencia" value={soloVigentes ? 'vigentes' : 'todas'} onChange={e => setSoloVigentes(e.target.value === 'vigentes')}
            options={[{ value: 'vigentes', label: 'Vigentes hoy' }, { value: 'todas', label: 'Todas (con historia)' }]} />
        </div>
        {!soloLectura && (
          <Button size="sm" leftIcon={<Plus size={15} />} onClick={() => { setBase(null); setFormAbierto(true); }}>Nueva tarifa</Button>
        )}
      </div>

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        {visibles.map(t => {
          const esVigente = vigente(t);
          const editando = editandoId === t.id;
          return (
            <li key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', background: 'var(--bg-raised)', border: '1px solid var(--border)', borderLeft: `3px solid ${esVigente ? '#10B981' : 'var(--border-strong)'}`, borderRadius: 'var(--radius-md)', padding: '0.625rem 0.875rem', opacity: t.active ? 1 : 0.6 }}>
              <div style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {editando ? (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input value={editNombre} onChange={e => setEditNombre(e.target.value)} style={{ flex: 1, padding: '0.35rem 0.6rem', fontSize: '13px', background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontFamily: 'inherit' }} />
                    <Button size="sm" leftIcon={<Check size={13} />} onClick={() => guardarNombre(t.id)} isLoading={enCurso}>Guardar</Button>
                    <Button size="sm" variant="ghost" leftIcon={<X size={13} />} onClick={() => setEditandoId(null)}>Cancelar</Button>
                  </div>
                ) : (
                  <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                    {t.name} <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>· {t.customerName}</span>
                    {t.vehicleClass && <span style={{ marginLeft: '0.5rem', fontSize: '11px', fontWeight: 700, color: 'var(--accent)' }}>{t.vehicleClass}</span>}
                    {!t.active && <span style={{ marginLeft: '0.5rem', fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)' }}>(inactiva)</span>}
                    {t.active && !esVigente && <span style={{ marginLeft: '0.5rem', fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)' }}>({t.validFrom > hoy ? 'futura' : 'cerrada'})</span>}
                  </span>
                )}
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{descripcionTarifa(t)}</span>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{vigenciaTexto(t)}</span>
              </div>
              {!soloLectura && !editando && (
                <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                  {t.active && !t.validTo && (
                    <Button size="sm" variant="secondary" leftIcon={<TrendingUp size={13} />} onClick={() => { setBase(t); setFormAbierto(true); }}>Subir precios</Button>
                  )}
                  <Button size="sm" variant="ghost" leftIcon={<Pencil size={13} />} onClick={() => { setEditandoId(t.id); setEditNombre(t.name); setAviso(null); }}>Renombrar</Button>
                  <Button size="sm" variant="ghost" leftIcon={t.active ? <EyeOff size={13} /> : <Eye size={13} />} onClick={() => alternar(t)} disabled={enCurso}>
                    {t.active ? 'Desactivar' : 'Reactivar'}
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {visibles.length === 0 && (
        <p style={{ margin: 0, padding: '1rem', textAlign: 'center', fontSize: '13px', color: 'var(--text-muted)' }}>
          {search || clienteFiltro ? 'Ninguna tarifa coincide.' : soloVigentes ? 'No hay tarifas vigentes hoy.' : 'Todavía no hay tarifas.'}
        </p>
      )}

      <TariffFormModal isOpen={formAbierto} onClose={() => setFormAbierto(false)} customers={customers} base={base} onCrear={crearConCierre} />
    </div>
  );
}
